'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import type { TraceFile } from '@terra/shared-schemas-ai';

type TraceFilesContextType = {
  traceFilesById: Map<string, TraceFile>;
  traceFilesByName: Map<string, TraceFile>;
  traceFiles: TraceFile[];
  getTraceFile: (idOrName: string) => TraceFile | undefined;
  findTraceFiles: (pattern: string | RegExp) => TraceFile[];
};

const TraceFilesContext = createContext<TraceFilesContextType | undefined>(
  undefined,
);

type TraceFilesProviderProps = {
  children: ReactNode;
  /**
   * Array of trace files to make available
   */
  traceFiles?: TraceFile[];
};

export function TraceFilesProvider({
  children,
  traceFiles = [],
}: TraceFilesProviderProps) {
  const traceFilesById = useMemo(() => {
    const map = new Map<string, TraceFile>();

    for (const file of traceFiles) {
      map.set(file.id, file);
    }

    return map;
  }, [traceFiles]);

  const traceFilesByName = useMemo(() => {
    const map = new Map<string, TraceFile>();

    for (const file of traceFiles) {
      map.set(file.name, file);
    }

    return map;
  }, [traceFiles]);

  const getTraceFile = useCallback(
    (idOrName: string): TraceFile | undefined => {
      const byId = traceFilesById.get(idOrName);

      if (byId) return byId;

      const byName = traceFilesByName.get(idOrName);

      if (byName) return byName;

      for (const [name, file] of traceFilesByName) {
        if (name.includes(idOrName) || idOrName.includes(name)) {
          return file;
        }
      }

      return undefined;
    },
    [traceFilesById, traceFilesByName],
  );

  const findTraceFiles = useCallback(
    (pattern: string | RegExp): TraceFile[] => {
      const regex =
        typeof pattern === 'string' ? new RegExp(pattern, 'i') : pattern;

      return traceFiles.filter(
        (file) => regex.test(file.name) || regex.test(file.id),
      );
    },
    [traceFiles],
  );

  const contextValue: TraceFilesContextType = useMemo(
    () => ({
      traceFilesById,
      traceFilesByName,
      traceFiles,
      getTraceFile,
      findTraceFiles,
    }),
    [
      traceFilesById,
      traceFilesByName,
      traceFiles,
      getTraceFile,
      findTraceFiles,
    ],
  );

  return (
    <TraceFilesContext.Provider value={contextValue}>
      {children}
    </TraceFilesContext.Provider>
  );
}

export function useTraceOptional() {
  const context = useContext(TraceFilesContext);

  if (context === undefined) {
    throw new Error('useTrace must be used within a TraceFilesProvider');
  }

  return context;
}

export function useTraceFilesOptional() {
  return useContext(TraceFilesContext);
}
